"use client";

import { useState } from "react";
import { DropdownMenue, MENUE_EINTRAG } from "./dropdown-menue";
import { Suchfeld } from "./formular";
import { TAG_PUNKT, type TagFarbe } from "./formular/auswahl-konfiguration";

/**
 * Tag-Menü (PM Paket 3b, B4, Design-Guide `tag-menue.md`): Aufklappmenü zur Pflege der Tags eines Objekts. Kopf „Tags“, Suchfeld,
 * Liste aller Tags des Projekts mit Haken (gesetzt = angehakt), darunter „Neu anlegen“, wenn der Suchtext noch kein Tag ist.
 * Ein Klick auf eine Zeile setzt oder löst das Tag SOFORT (der Aufrufer schreibt und lädt neu); das Menü bleibt offen, damit
 * mehrere Tags nacheinander gesetzt werden können.
 *
 * Der Baustein prüft nichts: der Aufrufer bietet nur Tags an, die die Action zulässt (r109/r110), und zeigt Ablehnung und
 * Fehlschlag als Satz am Feld (nicht im Menü). Farbe: Farbpunkt in einem von vier festen Tokens (AW-006, Katalogblock: Tag-Symbol
 * in Roh-Farben). Abweichung AW-023: „Neues Tag“ als Zeile im Menü statt Dialog; Schloss-Symbol „Private tag“ entfällt.
 */
export interface TagMenueEintrag {
  label: string;
  farbe: TagFarbe;
  gewaehlt: boolean;
  /**
   * Verwendung des Tags (Paket Tags T-c, Entscheid V4): Zahl der nicht gelöschten Aufgaben des Projekts, die das Tag tragen, DIESELBE
   * Zahl wie in der Verwaltung und im Brett-Filter. Fehlt sie, steht hinter dem Namen nichts.
   */
  anzahl?: number;
}

/** Der eine Hinweissatz zu Tags (Tag-Menü und Fenster „Tag anlegen/bearbeiten“, T-b): eine Wortquelle, keine zweite Fassung. */
export const TAG_HINWEIS = "Tags sind Sachbegriffe, keine Personen- oder Bewertungstags.";

const FLAECHE = "fixed top-0 start-0 z-70 bg-dropdown border border-dropdown-line rounded-xl shadow-lg divide-y divide-dropdown-divider";
const AUSLOESER =
  "py-1 px-2 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-lg border border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus disabled:opacity-50 disabled:pointer-events-none";

export function TagMenue({
  eintraege,
  onWaehle,
  onNeu,
  ausloeserLabel = "Tags bearbeiten",
  maxLaenge = 40,
  belegt = false,
  verwaltenHref,
}: {
  /**
   * Link „Tags verwalten“ am Ende des Menüs (Paket Tags T-c, Entscheid V4). Der Aufrufer setzt ihn nur, wenn die Person Tags
   * verwalten darf (gleiche Prüfung wie die Verwaltungs-Actions); ohne Wert gibt es die Zeile nicht (still, r110).
   */
  verwaltenHref?: string;
  eintraege: TagMenueEintrag[];
  /** Tag setzen bzw. lösen (`gewaehlt` = neuer Zustand). */
  onWaehle: (label: string, gewaehlt: boolean) => void;
  /** Neues Tag anlegen und setzen. */
  onNeu: (label: string) => void;
  ausloeserLabel?: string;
  maxLaenge?: number;
  /** Ein Schreibvorgang läuft: die Zeilen sind gesperrt. */
  belegt?: boolean;
}) {
  const [suche, setSuche] = useState("");
  const q = suche.trim();
  const gefiltert = eintraege.filter((e) => e.label.toLowerCase().includes(q.toLowerCase()));
  const gibtEsSchon = eintraege.some((e) => e.label.toLowerCase() === q.toLowerCase());
  const kannNeu = q.length > 0 && !gibtEsSchon;

  return (
    <DropdownMenue
      ausloeserKlasse={AUSLOESER}
      ausloeserLabel={ausloeserLabel}
      ausloeser={
        <>
          {/* Lucide „Tag“ als Inline-SVG */}
          <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
            <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
          </svg>
          Tags
        </>
      }
      rechts={false}
      flaecheKlasse={FLAECHE}
      beimOeffnen={() => setSuche("")}
    >
      {() => (
        <>
          {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-selection-menus/tag-selection-menu */}
          <div className="w-64">
            <div className="py-2 px-4">
              <span className="block font-medium text-sm text-foreground">Tags</span>
            </div>
            <div className="p-1">
              <Suchfeld id="tag-menue-suche" label="Tag suchen" groesse="kompakt" placeholder="Suchen" value={suche} maxLength={maxLaenge} autoFocus onChange={(e) => setSuche(e.target.value)} />
            </div>
            <p className="px-4 pt-2 text-xs text-muted-foreground-1">{TAG_HINWEIS}</p>
            <div className="p-1 pt-2 max-h-72 overflow-y-auto">
              <span className="mb-1 px-3 block text-xs text-muted-foreground-1">Alle Tags</span>
              <div className="space-y-0.5">
                {gefiltert.map((e) => (
                  <button
                    key={e.label}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={e.gewaehlt}
                    disabled={belegt}
                    className={MENUE_EINTRAG}
                    onClick={() => onWaehle(e.label, !e.gewaehlt)}
                  >
                    <span className={TAG_PUNKT[e.farbe]} aria-hidden="true" />
                    <span className="min-w-0 grow truncate">{e.label}</span>
                    {typeof e.anzahl === "number" && <span className="shrink-0 text-xs text-muted-foreground-1 tabular-nums">{e.anzahl}</span>}
                    {e.gewaehlt && (
                      <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                ))}
                {gefiltert.length === 0 && !kannNeu && <p className="px-3 py-2 text-sm text-muted-foreground-1">Keine Tags.</p>}
                {kannNeu && (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={belegt}
                    className={MENUE_EINTRAG}
                    onClick={() => {
                      onNeu(q);
                      setSuche("");
                    }}
                  >
                    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 12h8" />
                      <path d="M12 8v8" />
                    </svg>
                    <span className="min-w-0 grow truncate">{`„${q}“ neu anlegen`}</span>
                  </button>
                )}
              </div>
            </div>
            {verwaltenHref && (
              <div className="p-1">
                {/* Abweichung AW-065: Zeile „Tags verwalten“ am Ende des Menüs (Betreiber-Entscheid V4, 13.10.2026): Link auf die Verwaltung, nur mit Recht */}
                <a href={verwaltenHref} role="menuitem" className={MENUE_EINTRAG}>
                  <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 7h-9" />
                    <path d="M14 17H5" />
                    <circle cx="17" cy="17" r="3" />
                    <circle cx="7" cy="7" r="3" />
                  </svg>
                  <span className="min-w-0 grow truncate">Tags verwalten</span>
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </DropdownMenue>
  );
}
